import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Wand2, ArrowLeft } from "lucide-react";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/" className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Link>
              </Button>
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-primary">Terms of Service</h1>
                <p className="text-sm text-muted-foreground">Legal terms and conditions</p>
              </div>
            </div>
            <Navigation />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <div className="mb-8">
            <p className="text-muted-foreground">Last updated: January 19, 2025</p>
          </div>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">Agreement to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing and using AI Humanizer Text ("the Service"), you agree to be bound by these Terms of Service 
                ("Terms"). If you do not agree to these Terms, you may not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed">
                AI Humanizer Text is an online service that uses artificial intelligence to transform AI-generated content 
                into more natural, human-like writing. The Service is provided on a subscription and credit-based model.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">User Accounts</h2>
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  To use our Service, you must create an account by providing accurate and complete information. You are responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Notifying us immediately of any unauthorized use of your account</li>
                  <li>Providing accurate and up-to-date account information</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Acceptable Use</h2>
              <div className="space-y-4">
                <p className="text-muted-foreground">You agree not to use the Service to:</p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>Process content that is illegal, harmful, or violates third-party rights</li>
                  <li>Generate or humanize spam, malicious, or deceptive content</li>
                  <li>Attempt to reverse engineer or compromise our systems</li>
                  <li>Share your account credentials with others</li>
                  <li>Use automated tools to access the Service without permission</li>
                  <li>Process content containing personal information of others without consent</li>
                  <li>Violate any applicable laws or regulations</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Subscription and Payments</h2>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Pricing and Billing</h3>
                <p className="text-muted-foreground">
                  Our Service operates on a credit-based system with various subscription plans. 
                  Current pricing is available on our pricing page and may be updated from time to time.
                </p>
                
                <h3 className="text-xl font-semibold">Payment Terms</h3>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>Subscription fees are billed in advance on a recurring basis</li>
                  <li>All fees are non-refundable except as required by law</li>
                  <li>You authorize us to charge your payment method for all applicable fees</li>
                  <li>Price changes will be communicated at least 30 days in advance</li>
                </ul>

                <h3 className="text-xl font-semibold">Cancellation</h3>
                <p className="text-muted-foreground">
                  You may cancel your subscription at any time. Cancellation takes effect at the end of your current billing period, 
                  and you retain access to the Service until that time.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Intellectual Property</h2>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Our Rights</h3>
                <p className="text-muted-foreground">
                  The Service, including its algorithms, software, and technology, is owned by us and protected by intellectual property laws. 
                  You may not copy, modify, distribute, or create derivative works of our Service.
                </p>
                
                <h3 className="text-xl font-semibold">Your Content</h3>
                <p className="text-muted-foreground">
                  You retain ownership of the text you submit to our Service. By using our Service, you grant us a limited license 
                  to process your content to provide the humanization service. We do not claim ownership of your input or output text.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Service Availability</h2>
              <p className="text-muted-foreground leading-relaxed">
                While we strive to maintain high service availability, we do not guarantee uninterrupted access to the Service. 
                We may temporarily suspend or restrict access for maintenance, updates, or other operational reasons.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, 
                or consequential damages arising from your use of the Service. Our total liability shall not exceed 
                the amount you paid for the Service in the 12 months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Disclaimer of Warranties</h2>
              <p className="text-muted-foreground leading-relaxed">
                The Service is provided "as is" without warranties of any kind. We do not guarantee that the Service will be 
                error-free, secure, or continuously available. The humanized text output is generated by AI and should be 
                reviewed before use.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Termination</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may terminate or suspend your account at any time for violation of these Terms or for any other reason 
                at our sole discretion. Upon termination, your right to use the Service immediately ceases.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms shall be governed by and construed in accordance with applicable laws. 
                Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the appropriate courts.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of significant changes 
                by email or through the Service. Continued use of the Service after changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed">
                For questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-muted p-4 rounded-lg mt-4">
                <p className="font-medium">Email: legal@aihumanizertext.com</p>
                <p className="text-sm text-muted-foreground mt-2">
                  We will respond to legal inquiries within 5 business days.
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;